// src/utils/cacheManager.js

const CACHE_KEYS = {
  STRUCTURE_VERSION: "cache:structureVersion",
  PREFIX: {
    COURSE_META: "cd:courseMeta",
    COURSE_STRUCTURE: "cd:courseStructure",
    COURSE_LIST: "cl:courseList",
  },
};

const DEFAULT_TTL = {
  COURSE_META: 1000 * 60 * 30,
  COURSE_STRUCTURE: 1000 * 60 * 60 * 12,
  COURSE_LIST: 1000 * 60 * 5,
};

const CURRENT_VERSIONS = {
  COURSE_META: 1,
  COURSE_STRUCTURE: 4,
  COURSE_LIST: 1,
};

const safeJSONParse = (raw) => {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const safeKey = (value) =>
  String(value || "")
    .replace(/[^a-z0-9_-]/gi, "")
    .slice(0, 48);

const hashString = (str) => {
  let h = 5381;
  for (let i = 0; i < str.length; i += 1) {
    h = ((h << 5) + h) ^ str.charCodeAt(i);
  }
  return (h >>> 0).toString(16);
};

const getStorage = (storageType) =>
  storageType === "session" ? sessionStorage : localStorage;

const isFresh = (timestamp, ttl) => {
  if (!timestamp || !ttl) return false;
  const age = Date.now() - Number(timestamp);
  return age >= 0 && age < ttl;
};

export const getGlobalStructureVersion = () => {
  try {
    const raw = localStorage.getItem(CACHE_KEYS.STRUCTURE_VERSION);
    const parsed = safeJSONParse(raw);
    return parsed?.version || "1.0.0";
  } catch {
    return "1.0.0";
  }
};

export const incrementStructureVersion = () => {
  try {
    const current = getGlobalStructureVersion();
    const parts = current.split(".");
    const patch = parseInt(parts[2] || "0", 10);
    const newVersion = `${parts[0]}.${parts[1]}.${patch + 1}`;

    localStorage.setItem(
      CACHE_KEYS.STRUCTURE_VERSION,
      JSON.stringify({
        version: newVersion,
        updatedAt: Date.now(),
      }),
    );

    return newVersion;
  } catch (error) {
    console.error("Error incrementing structure version:", error);
    return null;
  }
};

export const invalidateAllStructures = () => {
  incrementStructureVersion();
};

export const readCache = (key, options = {}) => {
  const {
    storageType = "local",
    validateVersion = false,
    currentVersion = null,
  } = options;

  try {
    const storage = getStorage(storageType);
    const raw = storage.getItem(key);
    if (!raw) return null;

    const data = safeJSONParse(raw);
    if (!data || !data.ts) return null;

    const ttl = data.ttl || DEFAULT_TTL.COURSE_STRUCTURE;
    if (!isFresh(data.ts, ttl)) return null;

    if (currentVersion && data.cacheVersion !== currentVersion) return null;

    if (validateVersion) {
      const globalVersion = getGlobalStructureVersion();
      if (data.structureVersion !== globalVersion) return null;
    }

    return {
      ...data,
      isFresh: true,
    };
  } catch (error) {
    console.error("Error reading cache:", key, error);
    return null;
  }
};

export const writeCache = (key, data, options = {}) => {
  const {
    storageType = "local",
    ttl = DEFAULT_TTL.COURSE_STRUCTURE,
    includeStructureVersion = false,
    cacheVersion = null,
    maxSize = 3500000,
  } = options;

  try {
    const storage = getStorage(storageType);
    const payload = {
      ...data,
      ts: Date.now(),
      ttl,
    };

    if (cacheVersion) payload.cacheVersion = cacheVersion;
    if (includeStructureVersion) {
      payload.structureVersion = getGlobalStructureVersion();
    }

    const raw = JSON.stringify(payload);
    if (raw.length > maxSize) return false;

    storage.setItem(key, raw);
    return true;
  } catch (error) {
    console.error("Error writing cache:", key, error);
    return false;
  }
};

export const getStructureCacheKey = (courseId, userScope = "anon") =>
  `${CACHE_KEYS.PREFIX.COURSE_STRUCTURE}:v${CURRENT_VERSIONS.COURSE_STRUCTURE}:${courseId}:${safeKey(userScope)}`;

export const getCoursMetaCacheKey = (codigoCurso, userScope = "anon") =>
  `${CACHE_KEYS.PREFIX.COURSE_META}:v${CURRENT_VERSIONS.COURSE_META}:${safeKey(codigoCurso)}:${safeKey(userScope)}`;

export const getCourseListCacheKey = (role, userId) =>
  `${CACHE_KEYS.PREFIX.COURSE_LIST}:v${CURRENT_VERSIONS.COURSE_LIST}:${role}:${safeKey(userId)}`;

export const invalidateCourse = (courseId, userScope = null) => {
  try {
    if (userScope !== null && userScope !== undefined) {
      localStorage.removeItem(getStructureCacheKey(courseId, userScope));
      return;
    }

    const prefix = `${CACHE_KEYS.PREFIX.COURSE_STRUCTURE}:v${CURRENT_VERSIONS.COURSE_STRUCTURE}:${courseId}:`;
    Object.keys(localStorage)
      .filter((key) => key.startsWith(prefix))
      .forEach((key) => localStorage.removeItem(key));
  } catch (error) {
    console.error("Error invalidating course cache:", error);
  }
};

export const invalidateCourseMeta = (codigoCurso, userScope = null) => {
  try {
    if (userScope !== null && userScope !== undefined) {
      sessionStorage.removeItem(getCoursMetaCacheKey(codigoCurso, userScope));
      return;
    }

    const prefix = `${CACHE_KEYS.PREFIX.COURSE_META}:v${CURRENT_VERSIONS.COURSE_META}:${safeKey(codigoCurso)}:`;
    Object.keys(sessionStorage)
      .filter((key) => key.startsWith(prefix))
      .forEach((key) => sessionStorage.removeItem(key));
  } catch (error) {
    console.error("Error invalidating course meta cache:", error);
  }
};

export const invalidateCourseList = (role, userId) => {
  try {
    if (userId !== null && userId !== undefined) {
      sessionStorage.removeItem(getCourseListCacheKey(role, userId));
      return;
    }

    const prefix = `${CACHE_KEYS.PREFIX.COURSE_LIST}:v${CURRENT_VERSIONS.COURSE_LIST}:${role}:`;
    Object.keys(sessionStorage)
      .filter((key) => key.startsWith(prefix))
      .forEach((key) => sessionStorage.removeItem(key));
  } catch (error) {
    console.error("Error invalidating course list cache:", error);
  }
};

export const clearExpiredCache = () => {
  try {
    const cleanStorage = (storage, fallbackTtl) => {
      Object.keys(storage).forEach((key) => {
        if (!key.startsWith("cd:") && !key.startsWith("cl:")) return;
        const data = safeJSONParse(storage.getItem(key));
        if (!data || !data.ts) {
          storage.removeItem(key);
          return;
        }

        const ttl = data.ttl || fallbackTtl;
        if (!isFresh(data.ts, ttl)) storage.removeItem(key);
      });
    };

    cleanStorage(localStorage, DEFAULT_TTL.COURSE_STRUCTURE);
    cleanStorage(sessionStorage, DEFAULT_TTL.COURSE_META);
  } catch (error) {
    console.error("Error clearing expired cache:", error);
  }
};

export const clearAllCache = () => {
  try {
    Object.keys(localStorage)
      .filter((key) => key.startsWith("cd:") || key.startsWith("cl:"))
      .forEach((key) => localStorage.removeItem(key));

    Object.keys(sessionStorage)
      .filter((key) => key.startsWith("cd:") || key.startsWith("cl:"))
      .forEach((key) => sessionStorage.removeItem(key));
  } catch (error) {
    console.error("Error clearing all cache:", error);
  }
};

const cacheManager = {
  getGlobalStructureVersion,
  incrementStructureVersion,
  invalidateAllStructures,
  readCache,
  writeCache,
  invalidateCourse,
  invalidateCourseMeta,
  invalidateCourseList,
  clearExpiredCache,
  clearAllCache,
  getStructureCacheKey,
  getCoursMetaCacheKey,
  getCourseListCacheKey,
  hashString,
  safeKey,
  CACHE_KEYS,
  DEFAULT_TTL,
  CURRENT_VERSIONS,
};

export default cacheManager;
